

















































































import {
  DateTimeInterface,
  DiscordUserInterface,
  PersistedEventInterface,
  UserPromotionListEntry,
  UserPromotionMessage,
} from "@/types";
import Vue from "vue";
import Component from "vue-class-component";
import UserPromotionListItem from "./UserPromotionListItem.vue";
import UserPromotionMessageHistoryDialog from "./UserPromotionMessageHistoryDialog.vue";
import UserPromotionLeaderboard from "./UserPromotionLeaderboard.vue";
import moment, { Moment } from "moment";
import DateUtility from "../../../DateUtility";
import Discord from "../../../Discord";

@Component({
  components: {UserPromotionListItem, UserPromotionMessageHistoryDialog, UserPromotionLeaderboard},
})
export default class UserPromotion extends Vue {
  public eventsLoading: boolean = true;
  public messaging: boolean = false;
  public viewingMessages: boolean = false;
  public selectedUser: number | null = null;
  public selectedUserForViewing: UserPromotionListEntry | null = null;
  public comment: string | null = null;
  public upcomingEvents: PersistedEventInterface[] = [];
  public selectedEvent: PersistedEventInterface | null = null;
  public userPromotionList: UserPromotionListEntry[] = [];
  public activeTab: number = 0;
  public promotionSearch: string = "";
  public promotionListHeaders = [
    { text: "Name", value: "name", sortable: true },
    { text: "Events Played", value: "eventsPlayed", sortable: true },
    { text: "Last Signup", value: "lastSignup", sortable: true },
    { text: "Messaged", value: "messaged", sortable: true, align: "center" },
    { text: "Signed Up", value: "signedUp", sortable: true, align: "center" },
    { text: "Actions", value: "actions", sortable: false, align: "center" },
  ];

  public mounted() {
      this.fetchEvents();
      this.fetchUserPromotionList();
  }

  public async fetchEvents() {
    this.eventsLoading = true;

    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const startDateString = startDate.toISOString();

    try {
      const response = await this.axios.get("/arma/events", {
        params: {
          "approved": 1,
          "date[gte]": startDateString,
        },
      });
      this.eventsLoading = false;
      this.upcomingEvents = response.data;
    } catch (error) {
      this.$store.dispatch("showErrorSnackbar", "Error fetching events.");
    } finally {
      this.eventsLoading = false;
    }
  }

  public async fetchUserPromotionList() {
    try {
      const response = await this.axios.get("/users/userPromotionMessages");
      this.userPromotionList = response.data;

      this.userPromotionList.sort((a, b) => {
        const dateA = a.latestSignup !== null ? this.getDate(a.latestSignup) : null;
        const dateB = b.latestSignup !== null ? this.getDate(b.latestSignup) : null;

        if (dateA && dateB) {
          if (dateA.isAfter(dateB)) {
            return -1; // Sort `a` before `b`
          } else if (dateA.isBefore(dateB)) {
            return 1; // Sort `b` before `a`
          } else {
            // If dates are the same, sort by the number of events played
            return b.eventsPlayed - a.eventsPlayed;
          }
        } else if (dateA) {
          return -1; // `a` has a date, `b` does not
        } else if (dateB) {
          return 1; // `b` has a date, `a` does not
        } else {
          // If both are null, sort by the number of events played
          return b.eventsPlayed - a.eventsPlayed;
        }
      });
    } catch (error) {
      this.$store.dispatch("showErrorSnackbar", "Error fetching user promotion list.");
    }
  }

  public getDate(date: DateTimeInterface): Moment {
    return DateUtility.getDate(date);
  }

  public getPromotionMessage(userId: string, eventName: string): UserPromotionMessage | null {
    const userPromotionEntry = this.userPromotionList.find((entry) => entry.id === userId);
    if (userPromotionEntry === undefined) {
      return null;
    }

    // Filter all messages for this event and sort by timestamp to get the newest one
    const messagesForEvent = userPromotionEntry.promotionMessages.filter((entry) => entry.event === eventName);
    if (messagesForEvent.length === 0) {
      return null;
    }

    // Sort by timestamp descending (newest first) and return the first one
    messagesForEvent.sort((a, b) => {
      if (!a.timestamp || !b.timestamp) {
        return 0;
      }
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return dateB.getTime() - dateA.getTime();
    });

    return messagesForEvent[0];
  }

  public getMessage(userId: string, eventName: string): string | undefined {
    const message = this.getPromotionMessage(userId, eventName);
    if (message === null) {
      return;
    }

    return message.comment;
  }

  public hasBeenMessaged(userId: string, eventName: string): boolean {
    const message = this.getPromotionMessage(userId, eventName);
    if (message === null) {
      return false;
    }

    return true;
  }

  public isSignedUp(userId: string, eventName: string): boolean {
    const foundEvent = this.upcomingEvents.find((e) => e.name === eventName);
    if (!foundEvent) {
      return false;
    }

    // Loop through all slot groups and their slots
    const isSignedUp = foundEvent.slotGroups.some((slotGroup) => {
      return slotGroup.playerSlots.some((slot) => {
        // Compare as strings to handle large Discord IDs
        return slot.discordUser && String(slot.discordUser.id) === String(userId);
      });
    });

    return isSignedUp;
  }

  public getUserAvatar(userId: string, avatar: string): string {
    return Discord.avatarSourceUri(userId, avatar);
  }

  public getLastSignupColor(timestamp: number): string {
    if (!timestamp) {
      return "#9E9E9E"; // Gray for no signup
    }

    const now = Date.now();
    const daysSinceSignup = (now - timestamp) / (1000 * 60 * 60 * 24);

    if (daysSinceSignup <= 14) {
      return "#4CAF50"; // Green - within 2 weeks
    } else if (daysSinceSignup <= 90) {
      return "#8BC34A"; // Light green - within 3 months
    } else if (daysSinceSignup <= 180) {
      return "#FFC107"; // Amber - within 6 months
    } else if (daysSinceSignup <= 365) {
      return "#FF9800"; // Orange - within 1 year
    } else {
      return "#F44336"; // Red - over 1 year
    }
  }

  public sendMessage() {
    if (this.selectedUser === null || this.selectedEvent === null) {
      return;
    }

    const payload = {
      id: this.selectedUser,
      event: this.selectedEvent.id,
      comment: this.comment,
    };

    this.axios.post("/users/userPromotionMessages", payload).then(() => {
      this.messaging = false;
      this.comment = null;
      this.fetchUserPromotionList();
    });
  }

  public viewUserMessages(user: UserPromotionListEntry) {
    this.selectedUserForViewing = user;
    this.viewingMessages = true;
  }

  public viewUserMessagesById(userId: string) {
    const user = this.userPromotionList.find((u) => u.id === userId);
    if (user) {
      this.viewUserMessages(user);
    }
  }

  public customSort(items: any[], sortBy: string[], sortDesc: boolean[]) {
    return items.sort((a, b) => {
      for (let i = 0; i < sortBy.length; i++) {
        const sortKey = sortBy[i];
        const isDesc = sortDesc[i];

        let aVal;
        let bVal;

        // Use timestamp for lastSignup sorting instead of formatted string
        if (sortKey === "lastSignup") {
          aVal = a.lastSignupTimestamp;
          bVal = b.lastSignupTimestamp;
        } else {
          aVal = a[sortKey];
          bVal = b[sortKey];
        }

        // Handle null/undefined values
        if (aVal == null && bVal == null) {
          continue;
        }
        if (aVal == null) {
          return isDesc ? -1 : 1;
        }
        if (bVal == null) {
          return isDesc ? 1 : -1;
        }

        // Compare values
        let comparison = 0;
        if (aVal < bVal) {
          comparison = -1;
        } else if (aVal > bVal) {
          comparison = 1;
        }

        if (comparison !== 0) {
          return isDesc ? -comparison : comparison;
        }
      }
      return 0;
    });
  }

  public formatLastSignup(latestSignup: DateTimeInterface | null): string {
    if (!latestSignup) {
      return "Never";
    }
    const date = DateUtility.getDate(latestSignup);
    return date.toDate().toLocaleDateString();
  }

  public get promotionListTableData() {
    if (!this.selectedEvent) {
      return [];
    }

    return this.userPromotionList.map((user) => {
      const messaged = this.hasBeenMessaged(user.id, this.selectedEvent!.name);
      const signedUp = this.isSignedUp(user.id, this.selectedEvent!.name);
      const lastSignup = this.formatLastSignup(user.latestSignup);
      const lastSignupTimestamp = user.latestSignup ? DateUtility.getDate(user.latestSignup).valueOf() : 0;

      return {
        userId: user.id,
        name: user.name,
        avatar: user.avatar,
        playerTag: user.community && !user.community.mainCommunity ? user.community.tag : null,
        eventsPlayed: user.eventsPlayed,
        lastSignup,
        lastSignupTimestamp,
        messaged,
        signedUp,
      };
    });
  }
}
